import React from 'react'

import { useTranslation } from 'react-i18next'

import { AccountNumberInput, accountNumberRegionConfig, ArcadeFieldLabel, FieldLabel } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

import { FieldController } from '../FieldController/FieldController'
import { FormMaskedInput } from '../FormMaskedInput'

export type AccountNumberProps = {
  name: string
  region: 'US' | 'GB' | 'CA' | 'AU'
  label?: string
}

export const AccountNumber: React.FC<AccountNumberProps> = ({ name, region, label, ...rest }) => {
  const { t } = useTranslation()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const { format, validate } = accountNumberRegionConfig[region]

  if (isArcadeMigrationEnabled) {
    return (
      <ArcadeFieldLabel label={label ?? t('Account number', { context: 'label for account number form field' })}>
        <FieldController
          FieldComponent={AccountNumberInput}
          componentProps={{
            ...rest,
            placeholder: t('Full account number', { context: 'placeholder for account number form field' }),
            region,
          }}
          name={name}
          rules={{
            required: {
              value: true,
              message: t('Enter your account number.', { context: 'error message for not entering account number' }),
            },
            validate: (value: string) =>
              validate(value) ||
              t('Invalid account number.', { context: 'error message for entering an invalid account number' }),
          }}
        />
      </ArcadeFieldLabel>
    )
  }

  return (
    <FieldLabel label={label ?? t('Account number', { context: 'label for account number form field' })}>
      <FormMaskedInput
        {...rest}
        autoComplete="off"
        name={name}
        placeholder={t('Full account number', { context: 'placeholder for account number form field' })}
        format={format}
        rules={{
          required: {
            value: true,
            message: t('Enter your account number.', { context: 'error message for not entering account number' }),
          },
          validate: value =>
            validate(value) ||
            t('Invalid account number.', { context: 'error message for entering an invalid account number' }),
        }}
      />
    </FieldLabel>
  )
}
